/**
 * spreadsheetHtmlToPlate — converts genuine spreadsheet (Excel/Google Sheets)
 * clipboard HTML into a single Plate table node.
 *
 * Only invoked when `isSpreadsheetTableHtml` fires (see useClipboardHandlers);
 * document-sourced tables (Word, Google Docs, other rich-text editors) fall
 * through to the generic paste path instead, which preserves in-cell lists
 * and other document-level structure this converter doesn't handle.
 *
 * Covers:
 *  - Grid rectangularization: walks <tr>/<td> into a rectangular logical grid
 *    before emitting rows, so ragged/short rows still produce a valid table.
 *  - rowspan/colspan handling: merged cells KEEP their merge — the anchor cell
 *    carries top-level camelCase `colSpan`/`rowSpan` (the exact attributes
 *    @platejs/table's mergeTableCells writes and getColSpan/getRowSpan, the
 *    cell renderer, and plateToHtml read) and the covered grid slots emit no
 *    cell node, mirroring the library's merge model.
 *  - Cell fills: bgcolor attributes / background(-color) inline styles land on
 *    the same `background` cell attribute paintCellBackground writes.
 *  - Cell vertical-align: vertical-align style / legacy valign attribute land
 *    on the same `verticalAlign` cell attribute the cell-spread path reads.
 *  - colSizes derivation: reads <colgroup><col> widths (px) into the table
 *    node's colSizes array.
 *  - Cell-align fidelity: reads text-align from the <td>/<th> or its inner
 *    <p>/<div>, carried onto each paragraph's `align` property.
 */
export function isSpreadsheetTableHtml(html: string): boolean {
    if (!/<table[^>]*>/i.test(html)) return false;
    if (/<google-sheets-html-origin/i.test(html)) return true;
    if (/td\.xl\d+/i.test(html)) return true;
    if (/<table[^>]*\sclass=["']ExcelTable/i.test(html)) return true;
    if (/\sdata-sheets-value=/i.test(html)) return true;
    // Excel-only clipboard markers that appear WITHOUT the td.xlNN <style> block
    // (modern Excel, Excel-for-Web, legacy 2003/2007). A single-row copy often
    // omits that block, so detection used to fail and the paste fell to the
    // generic path that collapses a one-row table into a single cell. These
    // markers never occur in Google Docs / Word / generic document HTML, so
    // those keep routing to the generic path (preserving in-cell lists).
    if (/content\s*=\s*["']?Excel\.Sheet/i.test(html)) return true;
    if (/xmlns:x\s*=\s*["']urn:schemas-microsoft-com:office:excel/i.test(html)) return true;
    return false;
}

export function isTableHtml(html: string): boolean {
    return /<table[^>]*>/i.test(html) && /<t[dh][^>]*>/i.test(html);
}

// True when the clipboard HTML is essentially a single table with no other
// meaningful content next to it — the case the spreadsheet shortcut handler
// is built for. Pasting from a previous Slate-based editor (and other "rich
// document" sources) brings headings, paragraphs, AND tables in one blob; for
// those the generic deserialize path is correct. `spreadsheetHtmlToPlateTable`
// would otherwise collapse the entire paste down to just the first table.
export function isPureTableHtml(html: string): boolean {
    if (!isTableHtml(html)) return false;
    if (typeof DOMParser === 'undefined') return false;
    try {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const body = doc?.body;
        if (!body) return false;
        const tables = body.querySelectorAll('table');
        if (tables.length !== 1) return false;
        // Strip the table and see whether any visible text content remains.
        const clone = body.cloneNode(true) as HTMLElement;
        clone.querySelectorAll('table').forEach((t) => t.remove());
        const remaining = (clone.textContent ?? '').replace(/&nbsp;/g, ' ').trim();
        return remaining.length === 0;
    } catch {
        return false;
    }
}

// Walk a DOM node and produce Plate inline children (text with marks).
// <BR> elements are NOT emitted here — callers that want line-break handling
// (i.e. cellBlocks) split on <BR> at the block level instead.
function inlineChildren(node: Node): any[] {
    const results: any[] = [];
    for (const child of Array.from(node.childNodes)) {
        if (child.nodeType === Node.TEXT_NODE) {
            const t = child.textContent ?? '';
            if (t) results.push({ text: t });
            continue;
        }
        if (child.nodeType !== Node.ELEMENT_NODE) continue;
        const el = child as HTMLElement;
        const tag = el.tagName;

        // BR is intentionally skipped here. cellBlocks splits on BR at the
        // block level so each segment becomes its own paragraph, which is
        // structurally correct and avoids stray \n text leaves.
        if (tag === 'BR') continue;

        const nested = inlineChildren(el);
        if (nested.length === 0) continue;

        // Apply marks based on tag.
        const marks: Record<string, boolean | string> = {};
        if (tag === 'B' || tag === 'STRONG') marks.bold = true;
        if (tag === 'I' || tag === 'EM') marks.italic = true;
        if (tag === 'U') marks.underline = true;
        if (tag === 'S' || tag === 'DEL' || tag === 'STRIKE') marks.strikethrough = true;
        if (tag === 'CODE') marks.code = true;

        // Read marks from inline style too (Google Docs / Excel express formatting
        // via `style`, not tags). Mirrors the cell-level fidelity of the generic
        // deserialize path so a styled run inside a spreadsheet cell keeps the
        // same leaf marks it would outside one.
        const style = el.getAttribute('style') || '';
        if (/font-weight:\s*(bold|[7-9]\d\d)/i.test(style)) marks.bold = true;
        if (/font-style:\s*italic/i.test(style)) marks.italic = true;
        if (/text-decoration(?:-line)?:[^;]*underline/i.test(style)) marks.underline = true;
        if (/text-decoration(?:-line)?:[^;]*line-through/i.test(style)) marks.strikethrough = true;
        const color = style.match(/(?:^|;)\s*color:\s*([^;]+)/i)?.[1]?.trim();
        if (color) marks.color = color;
        const fontSize = style.match(/font-size:\s*([^;]+)/i)?.[1]?.trim();
        if (fontSize) marks.fontSize = fontSize;

        if (Object.keys(marks).length > 0) {
            for (const leaf of nested) {
                if (typeof leaf.text === 'string') {
                    results.push({ ...marks, ...leaf });
                } else {
                    results.push(leaf);
                }
            }
        } else {
            results.push(...nested);
        }
    }
    return results.length > 0 ? results : [{ text: '' }];
}

// Read text-align from an element's `align` attribute or inline style.
// Excel emits align="right" / Sheets emits style="text-align:right" on the <td>.
// Returns undefined for the default (left) so we don't stamp redundant align.
function readAlign(el: Element): 'right' | 'center' | 'justify' | undefined {
    const attr = (el.getAttribute('align') || '').toLowerCase();
    const style = (el as HTMLElement).getAttribute?.('style') || '';
    const fromStyle = style.match(/text-align:\s*(left|right|center|justify)/i)?.[1]?.toLowerCase();
    // Inline text-align wins over the legacy align attribute when both are set.
    const value = fromStyle || attr;
    return value === 'right' || value === 'center' || value === 'justify' ? value : undefined;
}

// Split a DOM node's child nodes into segments delimited by <BR> elements.
// Each segment is an array of nodes that form one line of content — including
// EMPTY segments between two adjacent <BR>s, which represent an intentional
// blank line (e.g. "A<br><br>B" must keep the blank paragraph between A and
// B). The only segment dropped is a single trailing empty one produced by a
// line-ending <BR> with nothing after it (e.g. "A<br>") — that's just how a
// line terminates, not an intentional extra blank line.
function splitOnBr(node: Element): Node[][] {
    const segments: Node[][] = [];
    let current: Node[] = [];
    for (const child of Array.from(node.childNodes)) {
        if (child.nodeType === Node.ELEMENT_NODE && (child as HTMLElement).tagName === 'BR') {
            segments.push(current);
            current = [];
        } else {
            current.push(child);
        }
    }
    segments.push(current);
    if (segments.length > 1 && segments[segments.length - 1].length === 0) {
        segments.pop();
    }
    return segments;
}

// Produce inline children from an array of DOM nodes (one BR-split segment).
function inlineChildrenFromNodes(nodes: Node[]): any[] {
    // Wrap in a temporary fragment-like container to reuse inlineChildren.
    // We call inlineChildren on each node individually and concatenate.
    const results: any[] = [];
    for (const node of nodes) {
        if (node.nodeType === Node.TEXT_NODE) {
            const t = node.textContent ?? '';
            if (t) results.push({ text: t });
        } else if (node.nodeType === Node.ELEMENT_NODE) {
            // Reuse inlineChildren for element nodes by wrapping in a dummy span.
            // Instead, call inlineChildren on the element directly (it handles marks).
            const leaves = inlineChildren(node);
            // inlineChildren returns [{text:''}] for empty nodes — skip those.
            if (!(leaves.length === 1 && leaves[0].text === '')) {
                results.push(...leaves);
            }
        }
    }
    return results.length > 0 ? results : [{ text: '' }];
}

// Convert a cell's inner DOM into Plate paragraph blocks. Cell text-align
// (from the <td>, or an inner <p>/<div> which takes precedence) is carried onto
// the paragraph node's `align` — the same property the toolbar AlignMenu sets —
// so a right-aligned spreadsheet column stays right-aligned after paste.
//
// <BR> elements inside a cell are treated as paragraph separators: the content
// before and after each <BR> becomes its own `p` block. This prevents stray
// `{text:'\n'}` leaves from appearing inside paragraph children.
function cellBlocks(td: Element): any[] {
    const blocks: any[] = [];
    const cellAlign = readAlign(td);

    // If the cell has <p> or <div> children, treat each as a separate paragraph.
    // Each child paragraph may itself contain <BR>s — split those too.
    const paras = Array.from(td.querySelectorAll(':scope > p, :scope > div'));
    if (paras.length > 0) {
        for (const p of paras) {
            const align = readAlign(p) ?? cellAlign;
            // Check whether this paragraph contains any direct <BR> children.
            const hasBr = Array.from(p.childNodes).some((c) => c.nodeType === Node.ELEMENT_NODE && (c as HTMLElement).tagName === 'BR');
            if (hasBr) {
                const segments = splitOnBr(p);
                for (const seg of segments) {
                    const children = inlineChildrenFromNodes(seg);
                    blocks.push({ type: 'p', children, ...(align ? { align } : {}) });
                }
            } else {
                blocks.push({ type: 'p', children: inlineChildren(p), ...(align ? { align } : {}) });
            }
        }
        return blocks;
    }

    // Fallback: the cell has no block-level children. Split on top-level <BR>s
    // to produce one paragraph per line segment.
    const hasBr = Array.from(td.childNodes).some((c) => c.nodeType === Node.ELEMENT_NODE && (c as HTMLElement).tagName === 'BR');
    if (hasBr) {
        const segments = splitOnBr(td);
        for (const seg of segments) {
            const children = inlineChildrenFromNodes(seg);
            blocks.push({ type: 'p', children, ...(cellAlign ? { align: cellAlign } : {}) });
        }
    } else {
        blocks.push({ type: 'p', children: inlineChildren(td), ...(cellAlign ? { align: cellAlign } : {}) });
    }

    return blocks;
}

// Parse a px width from a <col> element's style or width attribute.
// Returns 0 when no usable positive width is present.
function colPxWidth(col: Element): number {
    const styleWidth = (col as HTMLElement).style?.width || '';
    const fromStyle = parseInt(styleWidth, 10);
    if (Number.isFinite(fromStyle) && fromStyle > 0) return fromStyle;
    const attrWidth = parseInt(col.getAttribute('width') || '', 10);
    if (Number.isFinite(attrWidth) && attrWidth > 0) return attrWidth;
    return 0;
}

// Parse colspan / rowspan from a td/th element. Returns 1 when absent or invalid.
function spanAttr(td: Element, attr: 'colspan' | 'rowspan'): number {
    const v = parseInt(td.getAttribute(attr) || '1', 10);
    return Number.isFinite(v) && v >= 1 ? v : 1;
}

// Read an authored cell fill from a pasted td/th: a background/background-color
// inline style (Sheets, modern Excel) wins over the legacy `bgcolor` attribute
// (older Excel), matching CSS precedence and readAlign above. The value lands
// on the SAME `background` cell attribute paintCellBackground/CellBackgroundMenu
// write and the cell renderer + plateToHtml read (`background ?? backgroundColor`).
// Default-ish values (white/transparent) are treated as "no fill": Sheets stamps
// them on unfilled cells, and an explicit white would override the editor's
// default th tint.
function readCellBackground(td: Element): string | undefined {
    const style = (td as HTMLElement).getAttribute?.('style') || '';
    const fromStyle = style.match(/(?:^|;)\s*background(?:-color)?:\s*([^;]+)/i)?.[1]?.trim();
    const raw = fromStyle || (td.getAttribute('bgcolor') || '').trim();
    if (!raw || raw.toLowerCase().includes('url(')) return undefined;
    const v = raw.toLowerCase().replace(/\s+/g, '');
    if (['transparent', 'none', 'inherit', 'initial', 'unset'].includes(v)) return undefined;
    if (['white', '#fff', '#ffffff', 'rgb(255,255,255)'].includes(v)) return undefined;
    return raw;
}

// Read a cell's vertical alignment: the inline `vertical-align` style
// (Sheets, modern Excel) wins over the legacy `valign` attribute (older
// Excel), matching the align/background precedence above. Lands on the same
// `verticalAlign` cell attribute the cell-spread path (useClipboardHandlers'
// CELL_FORMAT_PROPS) and the cell renderer read. 'middle' is treated as "no
// override" — it's the browser default for td/th — so we don't stamp it on
// every cell (mirrors readAlign's 'left' default).
function readVerticalAlign(td: Element): 'top' | 'bottom' | undefined {
    const style = (td as HTMLElement).getAttribute?.('style') || '';
    const fromStyle = style.match(/vertical-align:\s*(top|middle|bottom)/i)?.[1]?.toLowerCase();
    const attr = (td.getAttribute('valign') || '').toLowerCase();
    const value = fromStyle || attr;
    return value === 'top' || value === 'bottom' ? value : undefined;
}

// A single empty <td> — reused wherever a ragged/covered grid slot needs a
// placeholder cell (spreadsheet HTML parsing, and the plain-text grid builder
// below).
function emptyCell(): any {
    return { type: 'td', children: [{ type: 'p', children: [{ text: '' }] }] };
}

export function spreadsheetHtmlToPlateTable(html: string): any | null {
    if (typeof DOMParser === 'undefined') return null;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const table = doc.querySelector('table');
    if (!table) return null;

    const trs = Array.from(table.querySelectorAll('tr'));
    if (trs.length === 0) return null;

    // ── Phase 1: build a rectangular logical grid ────────────────────────────
    // The grid maps (rowIndex, colIndex) → { td element, isSpanFill }.
    // `isSpanFill` marks cells that are occupied by a rowspan/colspan expansion
    // of a real cell rather than a new <td>/<th>. Those positions get empty
    // placeholder cells in the final Plate output.

    // First pass: determine grid dimensions by simulating the span layout.
    const rowCount = trs.length;
    // occupancy[r][c] = true once a cell claims that slot (via span expansion).
    const occupancy: boolean[][] = Array.from({ length: rowCount }, () => []);

    // Record the real cell at each grid slot.
    type GridCell = { el: Element; isPlaceholder: false } | { el: null; isPlaceholder: true };
    const grid: GridCell[][] = Array.from({ length: rowCount }, () => []);

    for (let r = 0; r < rowCount; r++) {
        const tr = trs[r];
        const tdEls = Array.from(tr.querySelectorAll(':scope > td, :scope > th'));
        let cellIdx = 0; // index into tdEls for the current row

        // Walk columns, skipping already-occupied slots (from prior rowspans).
        let c = 0;
        while (cellIdx < tdEls.length) {
            // Skip slots already occupied by a rowspan from a previous row.
            while (occupancy[r]?.[c]) c++;

            const td = tdEls[cellIdx];
            const colspan = spanAttr(td, 'colspan');
            const rowspan = spanAttr(td, 'rowspan');

            // Mark all slots this cell spans as occupied.
            for (let dr = 0; dr < rowspan; dr++) {
                for (let dc = 0; dc < colspan; dc++) {
                    const row = r + dr;
                    const col = c + dc;
                    if (row < rowCount) {
                        if (!occupancy[row]) occupancy[row] = [];
                        occupancy[row][col] = true;
                    }
                }
            }

            // Place the real cell at (r, c).
            grid[r][c] = { el: td, isPlaceholder: false };

            // Fill the spanned columns in this row with placeholders.
            for (let dc = 1; dc < colspan; dc++) {
                grid[r][c + dc] = { el: null, isPlaceholder: true };
            }
            // Fill the spanned rows (and their spanned columns) with placeholders.
            for (let dr = 1; dr < rowspan; dr++) {
                for (let dc = 0; dc < colspan; dc++) {
                    const row = r + dr;
                    if (row < rowCount) {
                        grid[row][c + dc] = { el: null, isPlaceholder: true };
                    }
                }
            }

            c += colspan;
            cellIdx++;
        }
        // Advance past any trailing rowspan-occupied slots in this row.
        // (No action needed — grid[r] already has all real cells placed.)
    }

    // Compute the actual column count from the grid (maximum row width).
    let columnCount = 0;
    for (let r = 0; r < rowCount; r++) {
        const rowWidth = grid[r].length;
        if (rowWidth > columnCount) columnCount = rowWidth;
    }
    if (columnCount === 0) return null;

    // ── Phase 2: build Plate rows, preserving merges ─────────────────────────
    // Merge model (mirrors @platejs/table's mergeTableCells, which the renderer,
    // computeCellIndices, and plateToHtml all consume): the anchor cell carries
    // top-level camelCase `colSpan`/`rowSpan` and the covered slots emit NO cell
    // node. Emitting placeholder cells next to a span-carrying anchor (the old
    // behaviour, minus the spans) would add phantom columns. Slots that are
    // empty because the source row was ragged — not span-covered — still pad
    // with an empty cell so the logical grid stays rectangular.

    // A row whose every slot is covered by spans from above (Excel emits bare
    // <tr></tr> under a full-width merge) would emit zero cells; sanitize would
    // then synthesize a phantom cell into the empty tr. Drop such rows and
    // shorten every rowSpan that crosses them — content-identical, since the
    // dropped row contributes nothing of its own.
    const droppedRows = new Set<number>();
    for (let r = 0; r < rowCount; r++) {
        let allCovered = columnCount > 0;
        for (let c = 0; c < columnCount; c++) {
            if (!grid[r][c] || !grid[r][c].isPlaceholder) {
                allCovered = false;
                break;
            }
        }
        if (allCovered) droppedRows.add(r);
    }

    const rows: any[] = [];
    for (let r = 0; r < rowCount; r++) {
        if (droppedRows.has(r)) continue;
        const gridRow = grid[r];
        const cells: any[] = [];
        for (let c = 0; c < columnCount; c++) {
            const slot = gridRow[c];
            if (!slot) {
                // Ragged source row — pad to keep the grid rectangular.
                cells.push(emptyCell());
            } else if (slot.isPlaceholder) {
                // Covered by a rowspan/colspan anchor — no cell node.
                continue;
            } else {
                const td = slot.el!;
                // Clamp spans to the copied grid (Excel can emit a rowspan that
                // overruns the copied selection), then shorten across any
                // dropped fully-covered rows.
                const colSpan = Math.min(spanAttr(td, 'colspan'), columnCount - c);
                const clampedRowSpan = Math.min(spanAttr(td, 'rowspan'), rowCount - r);
                let rowSpan = clampedRowSpan;
                for (let dr = r + 1; dr < r + clampedRowSpan; dr++) {
                    if (droppedRows.has(dr)) rowSpan -= 1;
                }
                const background = readCellBackground(td);
                const verticalAlign = readVerticalAlign(td);
                cells.push({
                    type: td.tagName === 'TH' ? 'th' : 'td',
                    ...(colSpan > 1 ? { colSpan } : {}),
                    ...(rowSpan > 1 ? { rowSpan } : {}),
                    ...(background ? { background } : {}),
                    ...(verticalAlign ? { verticalAlign } : {}),
                    children: cellBlocks(td),
                });
            }
        }
        rows.push({
            type: 'tr',
            children: cells.length ? cells : [emptyCell()],
        });
    }

    const node: any = { type: 'table', children: rows };

    // ── Phase 3: build colSizes from <col> elements ──────────────────────────
    // columnCount is now the correct logical column count from the span-expanded
    // grid, so colSizes will always have exactly columnCount entries.
    const cols = Array.from(table.querySelectorAll('colgroup > col'));
    if (cols.length > 0) {
        const widths = cols.map(colPxWidth);
        if (widths.some((w) => w > 0)) {
            node.colSizes = Array.from({ length: columnCount }, (_, i) => widths[i] ?? 0);
        }
    }

    return node;
}

// Caps for the plain-text grid table (RECUR-26973 review): an oversized grid
// is either a pasting accident or adversarial input, not something a Plate
// table should render. Beyond these, the caller falls back to a plain-text
// paste instead.
const MAX_TEXT_GRID_ROWS = 200;
const MAX_TEXT_GRID_COLS = 30;

// Build a Plate table node DIRECTLY from a plain-text grid (tab/CSV paste) —
// no HTML synthesis, since escaping arbitrary clipboard text into markup is
// an injection footgun. Mirrors spreadsheetHtmlToPlateTable's emitted shape
// (table > tr > td > p > text leaf) so the fragment survives
// sanitizePlateFragment unchanged. Plain text carries no marks, so every cell
// is a single unstyled leaf; ragged rows are padded to the widest row with
// emptyCell() so the grid stays rectangular. Returns null when the grid
// exceeds MAX_TEXT_GRID_ROWS/MAX_TEXT_GRID_COLS — the caller pastes as text.
export function textGridToPlateTable(rows: string[][]): any | null {
    const columnCount = rows.reduce((max, row) => Math.max(max, row.length), 0);
    if (rows.length > MAX_TEXT_GRID_ROWS || columnCount > MAX_TEXT_GRID_COLS) return null;
    return {
        type: 'table',
        children: rows.map((row) => ({
            type: 'tr',
            children: Array.from({ length: columnCount }, (_, c) =>
                c < row.length ? { type: 'td', children: [{ type: 'p', children: [{ text: row[c] }] }] } : emptyCell(),
            ),
        })),
    };
}

function textGridLines(text: string): string[] {
    return text.split(/\r\n|\r|\n/).filter((line) => line.length > 0);
}

// Minimal RFC-4180 line splitter: a `"…"` run quotes a field (commas inside
// don't split it) and `""` inside a quoted field is an escaped literal quote.
// Never spans multiple lines — callers only ever pass one line at a time.
function splitCsvLine(line: string): string[] {
    const fields: string[] = [];
    let field = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (inQuotes) {
            if (ch !== '"') {
                field += ch;
            } else if (line[i + 1] === '"') {
                field += '"';
                i++;
            } else {
                inQuotes = false;
            }
        } else if (ch === '"' && field === '') {
            inQuotes = true;
        } else if (ch === ',') {
            fields.push(field);
            field = '';
        } else {
            field += ch;
        }
    }
    fields.push(field);
    return fields;
}

// Tab-delimited (primary): needs at least one real tab and at least two
// content lines, with a consistent column count across rows — except the
// LAST row, which may be short, since a real paste often ends on a partial
// line. That trailing-short-row exception only applies once at least two
// full-width rows have established the grid shape (>= 3 lines); with exactly
// 2 lines there's nothing to compare the second line against except full
// equality — otherwise a single tabbed line ("Name:\tJohn Doe") followed by
// an unrelated line of prose reads as a 2-cell table.
//
// A genuine Excel/Sheets copy is RECTANGULAR DATA, not indented code: every
// row needs at least two real (non-blank) values, and no column may be blank
// on EVERY row. Tab-indented code (e.g. "\tif (x) {") produces a leading
// column that's blank on every line — that's indentation, not a column — and
// a line of only tabs (a blank line inside pasted code) has zero real values.
// When in doubt, paste as text.
function detectTabGridRows(lines: string[]): string[][] | null {
    if (lines.length < 2 || !lines.some((line) => line.includes('\t'))) return null;
    const rows = lines.map((line) => line.split('\t'));
    const widths = rows.map((row) => row.length);
    const bodyCount = lines.length >= 3 ? widths.length - 1 : widths.length;
    const bodyConsistent = widths.slice(0, bodyCount).every((w) => w === widths[0]);
    const trailingNotWider = lines.length >= 3 ? widths[widths.length - 1] <= widths[0] : true;
    if (!bodyConsistent || !trailingNotWider) return null;

    if (rows.some((row) => row.filter((cell) => cell.trim()).length < 2)) return null;
    const columnCount = widths[0];
    for (let c = 0; c < columnCount; c++) {
        if (rows.every((row) => !(row[c] ?? '').trim())) return null;
    }
    return rows;
}

// Comma-CSV (guarded — tried only once tab-delimited fails): needs at least
// 3 lines — with only 2, a short comma-containing phrase followed by an
// unrelated line reads as a table (the same vacuous-consistency-check hole
// the tab path had at 2 lines). Every line must split to the SAME field
// count (≥2, i.e. at least one real comma), and no field may read like
// prose, so an ordinary comma-containing sentence is never mistaken for
// tabular data.
function detectCsvGridRows(lines: string[]): string[][] | null {
    if (lines.length < 3) return null;
    const rows = lines.map(splitCsvLine);
    const width = rows[0].length;
    if (width < 2 || rows.some((row) => row.length !== width)) return null;
    const looksLikeProse = (field: string) => field.length > 120 || field.includes('. ');
    return rows.some((row) => row.some(looksLikeProse)) ? null : rows;
}

// STRICT gate for a plain-text (no-HTML) paste: tab-delimited wins when
// present; comma-CSV is only tried once that fails. Returns the parsed grid,
// or null to fall through to a normal text paste — when in doubt, paste as
// text.
export function detectTextGridRows(text: string): string[][] | null {
    const lines = textGridLines(text);
    return detectTabGridRows(lines) ?? detectCsvGridRows(lines);
}
